import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSearch } from "@fortawesome/free-solid-svg-icons";
import { searchOpportunitiesAPI } from "../../utils/requests/opportunitySearch";
import { OpportunityResultItem } from "../../components/OpportunitySearch/OpportunitySearch";
import Spinner from "../../components/spinner/Spinner";
import "./SearchOpportunitiesResultsPage.css";

const PAGE_SIZE = 20;
const MIN_QUERY_LENGTH = 2;

export default function SearchOpportunitiesResultsPage() {
  const [searchParams] = useSearchParams();
  const qRaw = searchParams.get("q") || "";
  const q = qRaw.trim();

  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (q.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setTotalCount(0);
      setLoading(false);
      setLoadingMore(false);
      setPage(1);
      return;
    }
    let cancelled = false;
    if (page === 1) setLoading(true);
    else setLoadingMore(true);
    searchOpportunitiesAPI(q, { page, page_size: PAGE_SIZE })
      .then((data) => {
        if (cancelled) return;
        const nextResults = data.results || [];
        setResults((prev) => (page === 1 ? nextResults : [...prev, ...nextResults]));
        setTotalCount(typeof data.count === "number" ? data.count : 0);
      })
      .catch(() => {
        if (!cancelled) {
          if (page === 1) setResults([]);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [q, page]);

  useEffect(() => {
    setPage(1);
  }, [q]);

  const onSelect = useCallback((opp) => {
    window.open(`/project_board/${opp.url_slug}`, "_blank", "noopener,noreferrer");
  }, []);

  const canLoadMore = !loading && !loadingMore && results.length < totalCount;
  const loadedCount = results.length;
  const remainingCount = Math.max(totalCount - loadedCount, 0);

  return (
    <div className="search-opportunities-results-page">
      <div className="search-opportunities-results-page__inner">

        <div className="search-opportunities-results-page__header">
          <h1 className="search-opportunities-results-page__title">Search results</h1>
          {q.length >= MIN_QUERY_LENGTH && q.length > 0 && (
            <p className="search-opportunities-results-page__query">
              &ldquo;{q}&rdquo;
            </p>
          )}
        </div>

        <div className="search-opportunities-results-page__top-row">
            <span className="search-opportunities-results-page__results-count">
              {!loading && q.length >= MIN_QUERY_LENGTH
                ? `${totalCount} ${totalCount === 1 ? "result" : "results"}`
              : "Searching..."}
          </span>
        </div>
        {q.length < MIN_QUERY_LENGTH && (
          <p className="search-opportunities-results-page__hint">
            Enter a search of at least {MIN_QUERY_LENGTH} characters on the{" "}
            <Link to="/search">search page</Link>.
          </p>
        )}

        {q.length >= MIN_QUERY_LENGTH && loading && (
          <div className="search-opportunities-results-page__loading">
            <Spinner />
          </div>
        )}

        {q.length >= MIN_QUERY_LENGTH && !loading && results.length === 0 && (
          <p className="search-opportunities-results-page__hint">No opportunities found for this search.</p>
        )}

        {q.length >= MIN_QUERY_LENGTH && !loading && results.length > 0 && (
          <>
            <div className="opportunity-search-results-full search-opportunities-results-page__list">
              {results.map((opp) => (
                <OpportunityResultItem
                  key={opp.id}
                  opp={opp}
                  onSelect={onSelect}
                  variant="fullPage"
                  isFocused={false}
                />
              ))}
            </div>

            {canLoadMore && (
              <div className="search-opportunities-results-page__load-more-wrap">
                <p className="search-opportunities-results-page__load-more-meta">
                  Showing {loadedCount} of {totalCount} results ({remainingCount} remaining)
                </p>
                <button
                  type="button"
                  className="search-opportunities-results-page__load-more-btn"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
